import { join as _join } from '@lodash';
import {
    OfferController,
    OfferMethods,
    OfferState,
    CurrentOfferStatus,
} from '@core/services/offer/_private/models/enums';
import DateUtils from '@utils/date/DateUtils';

type OfferRequestConstructorProps = Pick<OfferRequest, Exclude<keyof OfferRequest, 'langId'>>;

export default class OfferRequest {
    public controller?: string;
    public method?: string;
    public sportId?: number;
    public matchId?: string;
    public matchIds?: string;
    public tournamentId?: number;
    public compression?: boolean = true;
    public preselected?: boolean;
    public startDate?: string;
    public endDate?: string;
    public all?: boolean;
    public oddsresults?: boolean;
    public offerState?: OfferState;
    public currentStatus?: string;
    public live?: number;
    public langId = 2;
    public liveEventsNum?: boolean;

    constructor(props: OfferRequestConstructorProps) {
        Object.assign(this, props);
    }

    public toString() {
        return JSON.stringify(this);
    }

    static createAllEventsRequest(startDate: Date, endDate: Date): OfferRequest {
        return new OfferRequest({
            controller: OfferController.offer,
            method: OfferMethods.offerByDate,
            preselected: true,
            compression: true,
            offerState: OfferState.prematch,
            startDate: DateUtils.toString(DateUtils.inUtcTime(startDate)),
            endDate: DateUtils.toString(DateUtils.inUtcTime(endDate))
        });
    }

    static createSportRequest(sportId: number, startDate: Date, endDate: Date): OfferRequest {
        return new OfferRequest({
            sportId,
            controller: OfferController.offer,
            method: OfferMethods.offerByDate,
            offerState: OfferState.prematch,
            preselected: true,
            compression: true,
            startDate: DateUtils.toString(DateUtils.inUtcTime(startDate)),
            endDate: DateUtils.toString(DateUtils.inUtcTime(endDate))
        });
    }

    static createSocketLiveEventsRequest(startDate: Date): OfferRequest {
        return new OfferRequest({
            controller: OfferController.offer,
            method: OfferMethods.offerByDate,
            currentStatus: CurrentOfferStatus.active,
            offerState: OfferState.live,
            preselected: true,
            live: 1,
            compression: true,
            startDate: DateUtils.toString(DateUtils.inUtcTime(startDate))
        });
    }

    static createSuperEventsRequest(startDate: Date, endDate: Date): OfferRequest {
        return new OfferRequest({
            controller: OfferController.offer,
            method: OfferMethods.superOffer,
            startDate: DateUtils.toString(DateUtils.inUtcTime(startDate)),
            endDate: DateUtils.toString(DateUtils.inUtcTime(endDate)),
            all: true,
            offerState: OfferState.prematch,
            compression: true
        });
    }

    static createTournamentRequest(tournamentId: number, startDate: Date, endDate: Date): OfferRequest {
        return new OfferRequest({
            tournamentId,
            controller: OfferController.offer,
            method: OfferMethods.offerByDate,
            preselected: true,
            compression: true,
            offerState: OfferState.prematch,
            startDate: DateUtils.toString(DateUtils.inUtcTime(startDate)),
            endDate: DateUtils.toString(DateUtils.inUtcTime(endDate))
        });
    }

    static createTopTenRequest(): OfferRequest {
        return new OfferRequest({
            controller: OfferController.offer,
            method: OfferMethods.topTen,
            compression: true,
        });
    }

    static createSuperOfferRequest(startDate: Date, endDate: Date): OfferRequest {
        return new OfferRequest({
            controller: OfferController.offer,
            method: OfferMethods.superOffer,
            startDate: DateUtils.toString(DateUtils.inUtcTime(startDate)),
            endDate: DateUtils.toString(DateUtils.inUtcTime(endDate)),
            all: true,
            offerState: OfferState.prematch,
            compression: true,
        });
    }

    static createAllEventChangesRequest(): OfferRequest {
        return new OfferRequest({
            all: true,
            preselected: true
        });
    }

    static createEventRequest(eventId: number, shouldFetchOddResults: boolean = false): OfferRequest {
        return new OfferRequest({
            controller: OfferController.matches,
            method: OfferMethods.matchesByIds,
            matchIds: eventId.toString(),
            oddsresults: shouldFetchOddResults,
            offerState: OfferState.prematch,
        });
    }

    static createEventSubscription(eventId: number): OfferRequest {
        return new OfferRequest({
            matchId: eventId.toString(),
        });
    }

    static createEventsByIdRequest(eventIds: number[]): OfferRequest {
        return new OfferRequest({
            matchIds: _join(eventIds, ','),
            compression: true,
        });
    }

    static createSportsTreeRequest(startDate: Date) {
        return new OfferRequest({ startDate: DateUtils.toString(DateUtils.inUtcTime(startDate)) });
    }

    static createLiveEventsCountChangesRequest(liveEventsNum: boolean = true) {
        return new OfferRequest({ liveEventsNum });
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/core/services/offer/_private/models/OfferRequest.ts